import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { getLang, onLangChange } from '@/lib/lang';

import enCommon from './locales/en/common.json';
import enDashboard from './locales/en/dashboard.json';
import enProduction from './locales/en/production.json';
import enSetup from './locales/en/setup.json';
import enCatalog from './locales/en/catalog.json';
import enAdmin from './locales/en/admin.json';

import esCommon from './locales/es/common.json';
import esDashboard from './locales/es/dashboard.json';
import esProduction from './locales/es/production.json';
import esSetup from './locales/es/setup.json';
import esCatalog from './locales/es/catalog.json';
import esAdmin from './locales/es/admin.json';

export const NAMESPACES = ['common', 'dashboard', 'production', 'setup', 'catalog', 'admin'] as const;

// Catalogues are imported statically rather than lazy-loaded: the whole set is a few
// tens of KB, and bundling it means no async boundary, no <Suspense>, and no flash of
// untranslated text on first paint.
const resources = {
  en: {
    common: enCommon,
    dashboard: enDashboard,
    production: enProduction,
    setup: enSetup,
    catalog: enCatalog,
    admin: enAdmin,
  },
  es: {
    common: esCommon,
    dashboard: esDashboard,
    production: esProduction,
    setup: esSetup,
    catalog: esCatalog,
    admin: esAdmin,
  },
};

void i18next.use(initReactI18next).init({
  resources,
  lng: getLang(),
  fallbackLng: 'en',
  ns: NAMESPACES as unknown as string[],
  defaultNS: 'common',
  // REQUIRED, not cosmetic. react-i18next binds `t` to namespaces[0] only — passing
  // useTranslation(['dashboard','common']) does NOT search 'common' as you'd expect
  // (that needs nsMode: 'fallback'). Without this, every shared key referenced from
  // a page-namespaced component rendered as its raw key on screen, e.g. a chip
  // reading literally "type.generative". fallbackNS makes `common` the safety net
  // for every namespace, which is how the components are actually written.
  fallbackNS: 'common',
  // Keys are dot-namespaced (`rail.storyboard.label`), so nesting must stay off or
  // i18next would try to read them as an object path.
  keySeparator: false,
  nsSeparator: ':',
  interpolation: { escapeValue: false }, // React already escapes
  react: { useSuspense: false },
  // Surface a missing/typo'd key loudly in dev instead of silently rendering the key.
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: import.meta.env.DEV
    ? (lngs, ns, key) => {
        console.warn(`[i18n] missing key "${ns}:${key}" for ${lngs.join(', ')}`);
      }
    : undefined,
});

// The language store is the single source of truth (mirroring theme.ts); i18next
// follows it.
onLangChange((l) => {
  void i18next.changeLanguage(l);
});

export default i18next;
